import {InquirySearchResult, MemberInfoSummary, InquiryTypeSelectItem} from '@/types';
import {ActionContext} from 'vuex';
import {api} from '@/api';
import {
  // types
  UserRecentOrderCrm,
  GetMembersMemberNo,
  GetOrderOptionsUserSummaryCry,
  GetCouponsIssuesSummary,
  GetMembersMemberNoAccumulations,
  GetReviews,
  // response
  GetProductInquiries,
  GetInquiriesRequest,
  //request
  GetMembersMemberNoRequest,
  GetMembersMemberNoAccumulationsRequest,
  GetOrderOptionUserRecentOrderCrmRequest,
  GetProductInquiriesRequest,
  GetCouponsIssuesSummaryRequest,
  GetOrderOptionsUserSummaryCryRequest,
  GetInquiryTypeRequest,
  InquiryType,
  GetInquiryTypesInquiryTypeNoRequest,
  GetInquiryTypesInquiryTypeNo,
  GetInquiries,
  PutMembersMemberNoRequest,
  GetReviewsRequest,
  GetBanksRequest,
  Bank,
  CountryCode, GetAccumulationConfigurationsRequest
} from 'supporter';
import {addDay, getStrYMDHMSS} from '@/utils/dateUtils';

interface MemberCrmState {
  memberInfoDetail: GetMembersMemberNo;
  memberInfoSummary: MemberInfoSummary;
  userRecentOrderCrmList: UserRecentOrderCrm[];
  inquiryList: GetInquiries;
  productInquiryList: GetProductInquiries;
  userSummaryCrm: GetOrderOptionsUserSummaryCry;
  couponSummary: GetCouponsIssuesSummary;
  memberAccumulations: GetMembersMemberNoAccumulations;
  inquiryTypes: InquiryType[];
  inquiryTypeDetail: GetInquiryTypesInquiryTypeNo;
  postReviews: GetReviews;
  bankInfo: {
    countryCode: CountryCode;
    bankList: Bank[];
  };
  accumulationConfigurations: any[]
}

export default {
  namespaced: true,

  state: {
    memberInfoDetail: {
      memberNo: 0,
      memberName: '',
      mallNo: 0,
      memberId: '',
      nickname: '',
      gradeName: '',
      gradeNo: 0,
      gradeReevaluationDate: '',
      groupNames: [],
      certificated: false,
      certificatedDate: '',
      adultCertificated: false,
      adultCertificatedDate: '',
      dateOfBirth: '',
      gender: '',
      phoneNo: '',
      mobileNo: '',
      email: '',
      refundAccountBank: '',
      refundAccountNo: '',
      refundAccountOwnerName: '',
      zipCode: '',
      streetAddress: '',
      streetAddressDetail: '',
      landNumberAddress: '',
      landNumberAddressDetail: '',
      signUpDateTime: '',
      signUpRoute: '',
      lastLoginDateTime: '',
      lastLoginIpAddress: '',
      memberType: '',
      blacklisted: false,
      appPushAgreed: false,
      smsAgreed: false,
      directMailAgreed: false,
      memberStatus: '',
      recommendId: '',
      comment: '',
      personalInfoAgreement: {
        thirdPartyProvisionAgreed: false,
        processConsignmentAgreed: false,
        collectionAndUseAgreed: false
      },
      openIdDependents: [],
      smsAgreedDateTime: '',
      smsDisagreedDateTime: '',
      directMailAgreedDateTime: '',
      directMailDisagreedDateTime: '',
      appPushAgreedDateTime: '',
      appPushDisagreedDateTime: '',
    },
    memberInfoSummary: {
      memberNo: 0,
      memberName: '',
      memberType: '',
      gradeName: '',
      mallNo: 0,
      mallName: '',
      availableCouponCount: 0,
      accumulations: 0
    },
    userRecentOrderCrmList: [],
    inquiryList: {
      contents: [],
      totalCount: 0
    },
    productInquiryList: {
      contents: [],
      totalPage: 0,
      totalCount: 0
    },
    userSummaryCrm: {
      realOrderCnt: 0,
      realOrderOptionCnt: 0,
      totalOrderCnt: 0,
      totalPayAmt: 0
    },
    couponSummary: {
      availableCount: 0,
      usedCount: 0
    },
    inquiryTypes: [],
    inquiryTypeDetail: {
      no: 0,
      name: '',
      assignees: [],
      description: ''
    },
    postReviews: {
      contents: [],
      totalPage: 0,
      totalCount: 0
    },
    bankInfo: {
      countryCode: 'KR',
      bankList: []
    },
    accumulationConfigurations: []
  },

  getters: {
    memberInfoDetail(state: MemberCrmState): GetMembersMemberNo {
      return state.memberInfoDetail;
    },

    memberInfoSummary(state: MemberCrmState): MemberInfoSummary {
      return state.memberInfoSummary;
    },

    userRecentOrderCrmList(state: MemberCrmState): UserRecentOrderCrm[] {
      return state.userRecentOrderCrmList;
    },

    inquiryList(state: MemberCrmState): GetInquiries {
      return state.inquiryList;
    },

    searchInquiryResultList(state: MemberCrmState): InquirySearchResult[] {
      return state.inquiryList.contents.map(inquiry => ({
        ...inquiry,
        inquiryTypeName: state.inquiryTypes.find(inquiryType => inquiryType.no === inquiry.inquiryTypeNo).name,
        issuerId: `${inquiry.issuer.issuerEmail}(${inquiry.issuer.issuerNo})`,
        assigneeName: inquiry.assignee && inquiry.assignee.assigneeName ? inquiry.assignee.assigneeName : '',
      }));
    },

    productInquiryList(state: MemberCrmState): GetProductInquiries {
      return state.productInquiryList;
    },

    inquiryTypes(state: MemberCrmState): InquiryType[] {
      return state.inquiryTypes;
    },

    inquiryTypeDetail(state: MemberCrmState): GetInquiryTypesInquiryTypeNo {
      return state.inquiryTypeDetail;
    },

    inquiryTypesSelectList(state: MemberCrmState): InquiryTypeSelectItem[] {
      // TODO: api 문서 스펙과 supporter의 InquiryType 필드 명이 다름 임시로 any 처리
      return state.inquiryTypes.map((inquiryType: any) => {
        return {
          name: inquiryType.name,
          value: inquiryType.no,
          assignees: inquiryType.assignees.filter(assignee => {
            // 서버에서 빈 이름값 데이터가 있음. 걸러내기
            if (assignee.name !== '') {
              return assignee;
            }
          })
        };
      });
    },

    postReviews(state: MemberCrmState): GetReviews {
      return state.postReviews;
    },

    bankList(state: MemberCrmState): Bank[] {
      const removeBankTargets = ['ANONYMOUS', 'MIZUHO', 'UFJ', 'SMBC', 'RESONA', 'DIRECT'];
      return [{value: '', name: '은행선택'}, ...state.bankInfo.bankList.filter(bank => !removeBankTargets.includes(bank.value))];
    },

    accumulationConfigurations(state: MemberCrmState): any[] {
      return state.accumulationConfigurations;
    }
  },

  mutations: {
    SET_MEMBER_INFO_SUMMARY<T extends GetMembersMemberNo & {mallName: string}>(state: MemberCrmState, data: T): void {
      const {memberNo, memberName, memberType, mallNo, gradeName, email, mallName} = data;
      const availableCouponCount = state.couponSummary.availableCount;
      const {totalPayAmt, realOrderCnt} = state.userSummaryCrm;
      const accumulations = state.memberAccumulations.availableAmount;

      state.memberInfoSummary = {
        memberNo,
        memberName,
        memberType,
        mallNo,
        mallName,
        gradeName,
        email,
        totalPayAmt,
        realOrderCnt,
        availableCouponCount,
        accumulations
      };
    },

    SET_USER_RECENT_ORDER_CRM_LIST(state: MemberCrmState, data: UserRecentOrderCrm[]): void {
      state.userRecentOrderCrmList = data;
    },

    SET_INQUIRY_LIST(state: MemberCrmState, data: GetInquiries): void {
      state.inquiryList = data;
    },

    SET_PRODUCT_INQUIRY_LIST(state: MemberCrmState, data: GetProductInquiries): void {
      state.productInquiryList = data;
    },

    SET_COUPON_SUMMARY(state: MemberCrmState, data: GetCouponsIssuesSummary): void {
      state.couponSummary = data;
    },

    SET_MEMBER_ACCUMULATIONS(state: MemberCrmState, data: GetMembersMemberNoAccumulations): void {
      state.memberAccumulations = data;
    },

    SET_USER_SUMMARY_CRM(state: MemberCrmState, data: GetOrderOptionsUserSummaryCry): void {
      state.userSummaryCrm = data;
    },

    SET_INQUIRY_TYPES(state: MemberCrmState, data: InquiryType[]): void {
      state.inquiryTypes = data;
    },

    SET_MEMBER_DETAIL_INFO(state: MemberCrmState, data: GetMembersMemberNo): void {
      state.memberInfoDetail = data;
    },

    SET_INQUIRY_TYPE_DETAIL(state: MemberCrmState, data: GetInquiryTypesInquiryTypeNo): void {
      state.inquiryTypeDetail = data;
    },

    SET_POST_REVIEWS(state: MemberCrmState, data: GetReviews): void {
      state.postReviews = data;
    },

    SET_BANKS(state: MemberCrmState, data: Bank[]) {
      state.bankInfo.bankList = data;
    },

    SET_ACCUMULATION_CONFIGURATIONS(state: MemberCrmState, data: any[]){
      state.accumulationConfigurations = data;
    }
  },

  actions: {
    async fetchInquiryTypeByNo({state, commit}: ActionContext<MemberCrmState, any>, payload: GetInquiryTypesInquiryTypeNoRequest): Promise<void> {
      if (!payload.pathParams.inquiryTypeNo) {
        commit('SET_INQUIRY_TYPE_DETAIL', {
          no: 0,
          channel: '',
          name: '',
          assignees: [],
          description: ''
        });
        return;
      }

      const inquiryTypesByNoResponse = await api.getInquiryTypesInquiryTypeNo(payload);
      commit('SET_INQUIRY_TYPE_DETAIL', inquiryTypesByNoResponse.data);
    },

    async fetchInquiryTypes({state, commit}: ActionContext<MemberCrmState, any>, payload: GetInquiryTypeRequest): Promise<void> {
      const inquiryTypesResponse = await api.getInquiryType(payload);
      commit('SET_INQUIRY_TYPES', inquiryTypesResponse.data);
    },

    async fetchMemberByMemberNo({state, commit}: ActionContext<MemberCrmState, any>, payload: GetMembersMemberNoRequest): Promise<void> {
      const memberResponse = await api.getMembersMemberNo(payload);
      commit('SET_MEMBER_DETAIL_INFO', memberResponse.data);
    },

    async fetchAccumulations({state, commit}: ActionContext<MemberCrmState, any>, payload: GetMembersMemberNoAccumulationsRequest): Promise<void> {
      const memberAccumulationsResponse = await api.getMembersMemberNoAccumulations(payload);
      commit('SET_MEMBER_ACCUMULATIONS', memberAccumulationsResponse.data);
    },

    async fetchRecentOrderCrms({state, commit}: ActionContext<MemberCrmState, any>, payload: GetOrderOptionUserRecentOrderCrmRequest): Promise<void> {
      const userRecentOrderResponse = await api.getOrderOptionUserRecentOrderCrm(payload);
      commit('SET_USER_RECENT_ORDER_CRM_LIST', userRecentOrderResponse.data);
    },

    async fetchInquiries({state, commit}: ActionContext<MemberCrmState, any>, payload: GetInquiriesRequest): Promise<void> {
      const userRecentInquiryResponse = await api.getInquiries(payload);
      commit('SET_INQUIRY_LIST', userRecentInquiryResponse.data);
    },

    async fetchProductInquires({state, commit}: ActionContext<MemberCrmState, any>, payload: GetProductInquiriesRequest): Promise<void> {
      const userRecentProductInquiryResponse = await api.getProductInquiries(payload);
      commit('SET_PRODUCT_INQUIRY_LIST', userRecentProductInquiryResponse.data);
    },

    async fetchCouponsIssuesSummary({state, commit}: ActionContext<MemberCrmState, any>, payload: GetCouponsIssuesSummaryRequest): Promise<void> {
      const couponSummaryResponse = await api.getCouponsIssuesSummary(payload);
      commit('SET_COUPON_SUMMARY', couponSummaryResponse.data);
    },

    async fetchUserSummaryCrm({state, commit}: ActionContext<MemberCrmState, any>, payload: GetOrderOptionsUserSummaryCryRequest): Promise<void> {
      const userSummaryCrmResponse = await api.getOrderOptionsUserSummaryCry(payload);
      commit('SET_USER_SUMMARY_CRM', userSummaryCrmResponse.data);
    },

    async putMemberByMemberNo({state, commit}: ActionContext<MemberCrmState, any>, payload: PutMembersMemberNoRequest): Promise<void> {
      const putMembersMemberNoResponse = await api.putMembersMemberNo(payload);
    },

    async fetchAccumulationConfig({state, commit}: ActionContext<MemberCrmState, any>, payload: GetAccumulationConfigurationsRequest): Promise<void> {
      const accumulationsConfigurations = await api.getAccumulationsConfigurations(payload);
      commit('SET_ACCUMULATION_CONFIGURATIONS', accumulationsConfigurations.data);
    },

    async fetchMemberCrmSummary({state, rootGetters, dispatch, commit}: ActionContext<MemberCrmState, any>, payload: {memberNo: string}): Promise<void> {
      const sevenDaysAgo = getStrYMDHMSS(addDay(new Date(), -7, '-'), '-');
      const ORDER_LIST_COUNT = 5,
        INQUIRY_LIST_COUNT = 3,
        PRODUCT_INQUIRY_SIZE = 3;
      const {memberNo} = payload;

      await dispatch('fetchMemberByMemberNo', {pathParams: {memberNo}});

      await dispatch('fetchAccumulations', {pathParams: {memberNo}});

      const mallNo = state.memberInfoDetail.mallNo;
      await dispatch('fetchRecentOrderCrms', {
        params: {
          mallNo: mallNo.toString(),
          memberNo: parseInt(memberNo),
          count: ORDER_LIST_COUNT
        }
      });

      await dispatch('fetchInquiries', {
        params: {
          mallNo,
          issuerNo: memberNo,
          startDateTime: sevenDaysAgo,
          pageSize: INQUIRY_LIST_COUNT
        }
      });

      await dispatch('fetchProductInquires', {
        params: {
          mallNo: mallNo.toString(),
          memberNo,
          startYmd: sevenDaysAgo.slice(0, 10),
          size: PRODUCT_INQUIRY_SIZE
        }
      });

      await dispatch('fetchCouponsIssuesSummary', {
        params: {
          memberNo: parseInt(memberNo)
        }
      });

      await dispatch('fetchUserSummaryCrm', {
        params: {
          mallNo: mallNo.toString(),
          memberNo: parseInt(memberNo)
        }
      });

      commit('SET_MEMBER_INFO_SUMMARY', {...state.memberInfoDetail, mallName: rootGetters['mall/mallByMallNo'](mallNo).mallName});
    },

    async fetchPostReviews({state, commit}: ActionContext<MemberCrmState, any>, payload: GetReviewsRequest): Promise<void> {
      const reviewsResponse = await api.getReviews(payload);
      commit('SET_POST_REVIEWS', reviewsResponse.data);
    },

    async fetchBanks({state, commit}: ActionContext<MemberCrmState, any>, payload: GetBanksRequest): Promise<void> {
      const reviewsResponse = await api.getBanks(payload);
      commit('SET_BANKS', reviewsResponse.data); // TODO: JP를 지원하게 되면 countryCode도 set 하기
    }
  }
};
